import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff, Loader2, Volume2, Phone, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const VoiceMessageButton = ({
  onTranscript,
  onResponse,
  onError,
  className = '',
  language = 'fr'
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [assistantText, setAssistantText] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const sessionIdRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    console.log('🎤 [VoiceMessage] startRecording called');
    try {
      setError(null);
      setTranscript(null);
      setAssistantText(null);
      
      console.log('🎤 [VoiceMessage] Requesting microphone...');
      
      // Request microphone with Android-safe constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: true,
          channelCount: 1
        }
      });
      
      console.log('🎤 [VoiceMessage] Got microphone stream');

      // Create MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm';
      console.log('🎤 [VoiceMessage] Using mimeType:', mimeType);
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        console.log('🎤 [VoiceMessage] Data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('🎤 [VoiceMessage] Recording stopped, chunks:', audioChunksRef.current.length);
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Create blob from chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('🎤 [VoiceMessage] Audio blob size:', audioBlob.size);
        
        if (audioBlob.size > 0) {
          await sendAudioToBackend(audioBlob);
        }
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      console.log('🎤 [VoiceMessage] Recording started!');
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 30) {
            // Auto-stop at 30 seconds
            stopRecording();
            return 30;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (err) {
      console.error('🎤 [VoiceMessage] Error starting recording:', err);
      if (err.name === 'NotAllowedError') {
        setError('🎤 Microphone bloqué. Autorisez l\'accès au micro.');
      } else if (err.name === 'NotFoundError') {
        setError('🎤 Aucun microphone détecté.');
      } else {
        setError('Erreur: ' + err.message);
      }
      onError?.(err);
    }
  }, [onError]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    setIsRecording(false);
  }, []);

  const sendAudioToBackend = async (audioBlob) => {
    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', language);
      if (sessionIdRef.current) {
        formData.append('session_id', sessionIdRef.current);
      }

      const response = await fetch(`${API_URL}/api/voice-message`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Store session ID for conversation continuity
        sessionIdRef.current = data.session_id;
        
        setTranscript(data.transcript);
        setAssistantText(data.assistant_text);
        
        onTranscript?.(data.transcript);
        onResponse?.(data.assistant_text);

        // Play audio response
        if (data.audio_base64) {
          playAudioResponse(data.audio_base64);
        }
      } else {
        setError(data.error || 'Erreur de traitement');
      }

    } catch (err) {
      console.error('Error sending audio:', err);
      setError('Erreur: ' + err.message);
      onError?.(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const playAudioResponse = (base64Audio) => {
    try {
      setIsPlaying(true);
      
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audioRef.current = audio;
      
      audio.onended = () => {
        setIsPlaying(false);
      };
      
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setIsPlaying(false);
      };
      
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        setIsPlaying(false);
      });
      
    } catch (err) {
      console.error('Error creating audio:', err);
      setIsPlaying(false);
    }
  };

  const handleClick = () => {
    console.log('🎤 [VoiceMessage] Button clicked! State:', { isRecording, isProcessing, isPlaying });
    if (isRecording) {
      console.log('🎤 [VoiceMessage] Stopping recording...');
      stopRecording();
    } else if (!isProcessing && !isPlaying) {
      console.log('🎤 [VoiceMessage] Starting recording...');
      startRecording();
    } else {
      console.log('🎤 [VoiceMessage] Cannot start - busy');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      {/* Status display */}
      {(transcript || assistantText) && (
        <div className="w-full max-w-xs bg-white rounded-lg shadow-lg p-3 text-sm">
          {transcript && (
            <div className="mb-2">
              <span className="font-medium text-blue-600">Vous:</span>
              <p className="text-gray-700">{transcript}</p>
            </div>
          )}
          {assistantText && (
            <div>
              <span className="font-medium text-green-600">Assistant:</span>
              <p className="text-gray-700">{assistantText}</p>
            </div>
          )}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-2 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Main button */}
      <div className="relative">
        <Button
          onClick={handleClick}
          disabled={isProcessing}
          className={`
            w-16 h-16 rounded-full shadow-lg transition-all duration-300
            ${isRecording 
              ? 'bg-red-500 hover:bg-red-600 animate-pulse ring-4 ring-red-300' 
              : isProcessing
                ? 'bg-yellow-500'
                : isPlaying
                  ? 'bg-green-500 ring-4 ring-green-300'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
            }
          `}
          data-testid="voice-message-btn"
        >
          {isProcessing ? (
            <Loader2 className="w-7 h-7 text-white animate-spin" />
          ) : isRecording ? (
            <MicOff className="w-7 h-7 text-white" />
          ) : isPlaying ? (
            <Volume2 className="w-7 h-7 text-white animate-pulse" />
          ) : (
            <Mic className="w-7 h-7 text-white" />
          )}
        </Button>

        {/* Recording timer */}
        {isRecording && (
          <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2">
            <span className="text-xs font-mono bg-red-500 text-white px-2 py-0.5 rounded-full">
              {formatTime(recordingTime)}
            </span>
          </div>
        )}
      </div>

      {/* Label */}
      <div className="text-center mt-2">
        <span className="text-xs font-medium text-gray-600">
          {isRecording 
            ? 'Enregistrement... Appuyez pour envoyer'
            : isProcessing
              ? 'Traitement en cours...'
              : isPlaying
                ? 'Réponse en cours...'
                : 'Message vocal (mode fiable)'
          }
        </span>
      </div>
    </div>
  );
};

export default VoiceMessageButton;
